import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX, Radio, ChevronRight, Share2, Copy, Check, X, Code2, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { API } from "@/lib/api";
import Top40Section from "@/components/Top40Section";
import VoiceDropBox from "@/components/VoiceDropBox";
import ChannelSelector from "@/components/ChannelSelector";
import KingRequestModal from "@/components/KingRequestModal";
import useSecretUnlock from "@/hooks/useSecretUnlock";

const CHANNEL_LS_KEY = "mixxflow_channel";

export default function PlayerPage() {
  const audioRef = useRef(null);
  const [nowPlaying, setNowPlaying] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(0.85);
  const [muted, setMuted] = useState(false);
  const [currentMixId, setCurrentMixId] = useState(null);
  const [streamError, setStreamError] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [shareTab, setShareTab] = useState("share"); // 'share' | 'qr' | 'embed'
  const [kingOpen, setKingOpen] = useState(false);
  const { bind: secretBind, holdProgress, isHolding } = useSecretUnlock(() => setKingOpen(true));
  const [channel, setChannel] = useState(() => {
    if (typeof window === "undefined") return "all";
    // Priority: ?ch= URL param > localStorage > "all"
    try {
      const urlCh = new URLSearchParams(window.location.search).get("ch");
      if (urlCh) return urlCh.toLowerCase();
    } catch (e) { /* ignore */ }
    return localStorage.getItem(CHANNEL_LS_KEY) || "all";
  });

  // Fetch now-playing for the active channel
  const fetchNowPlaying = useCallback(async () => {
    try {
      const { data } = await api.get(`/now-playing?channel=${encodeURIComponent(channel)}`);
      setNowPlaying(data);
      return data;
    } catch (e) {
      console.error("now-playing fetch failed", e);
      return null;
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => {
    fetchNowPlaying();
    const id = setInterval(fetchNowPlaying, 10000);
    return () => clearInterval(id);
  }, [fetchNowPlaying]);

  // Persist any channel change (including URL-derived) to localStorage
  useEffect(() => {
    try { localStorage.setItem(CHANNEL_LS_KEY, channel); } catch (e) { /* ignore */ }
  }, [channel]);

  // When current track changes on server, swap audio source (if playing)
  useEffect(() => {
    if (!nowPlaying?.playing || !nowPlaying?.current) return;
    const newId = nowPlaying.current.id;
    if (newId !== currentMixId && isPlaying) {
      loadAndPlay(nowPlaying);
    }
    setCurrentMixId(newId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nowPlaying?.current?.id]);

  // Volume sync
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = muted ? 0 : volume;
    }
  }, [volume, muted]);

  const loadAndPlay = async (np) => {
    if (!np?.playing || !np?.current) return;
    const audio = audioRef.current;
    if (!audio) return;
    setStreamError("");
    // Use audio_url from server (Cloudinary CDN URL when available, /api fallback otherwise)
    const audioSrc = np.current.audio_url?.startsWith("http")
      ? np.current.audio_url
      : `${API}/mixes/${np.current.id}/audio`;
    audio.src = audioSrc;
    audio.load();
    const onReady = () => {
      try {
        audio.currentTime = np.offset || 0;
      } catch (e) { /* ignore */ }
      audio.play()
        .then(() => setIsPlaying(true))
        .catch((err) => {
          console.error("play failed", err);
          setStreamError("Tap play to start the broadcast.");
          setIsPlaying(false);
        });
      audio.removeEventListener("loadedmetadata", onReady);
    };
    audio.addEventListener("loadedmetadata", onReady);
  };

  const handlePlay = async () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
      return;
    }
    // Re-sync from server so we join the live broadcast where it actually is
    const np = await fetchNowPlaying();
    if (!np?.playing) {
      setStreamError(np?.message || "Broadcast not available.");
      return;
    }
    loadAndPlay(np);
  };

  const handleTrackEnded = async () => {
    // Track finished - re-fetch and play next from current server position
    const np = await fetchNowPlaying();
    if (np?.playing) loadAndPlay(np);
  };

  const current = nowPlaying?.current;
  const next = nowPlaying?.next;
  const hasPlaylist = nowPlaying?.playing && current;

  // Share helpers — channel-aware. "All" → bare URL, otherwise append ?ch=<channel>
  const stationOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const channelLabel = channel === "all" ? "All Genres" : channel.charAt(0).toUpperCase() + channel.slice(1);
  const stationUrl = channel === "all" ? stationOrigin : `${stationOrigin}/?ch=${channel}`;
  const embedUrl = channel === "all" ? `${stationOrigin}/embed` : `${stationOrigin}/embed?ch=${channel}`;
  const shareTitle = `Mixxflow DJ Radio — ${channelLabel} Channel`;
  const shareText = current
    ? `🔊 Now on Mixxflow DJ Radio · ${channelLabel} channel: "${current.title}" by ${current.artist}. Tune in →`
    : `🔊 Tune in to Mixxflow DJ Radio — ${channelLabel} channel · Powered by JAVI G →`;

  const handleShareClick = async () => {
    // Native share sheet on mobile / supported browsers
    if (navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url: stationUrl });
        return;
      } catch (e) {
        // user cancelled or failed — fall through to popover
      }
    }
    setShareOpen((v) => !v);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${shareText} ${stationUrl}`);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1800);
    } catch (e) {
      setShareCopied(false);
    }
  };

  const embedSnippet = `<iframe src="${embedUrl}" width="100%" height="120" frameborder="0" allow="autoplay" style="border:0;border-radius:16px;max-width:480px" title="Mixxflow DJ Radio — ${channelLabel}"></iframe>`;

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedSnippet);
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 1800);
    } catch (e) {
      setEmbedCopied(false);
    }
  };

  const encodedText = encodeURIComponent(shareText);
  const encodedUrl = encodeURIComponent(stationUrl);
  const shareLinks = [
    { name: "WhatsApp", url: `https://wa.me/?text=${encodedText}%20${encodedUrl}`, testId: "share-whatsapp" },
    { name: "X / Twitter", url: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, testId: "share-twitter" },
    { name: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`, testId: "share-facebook" },
    { name: "Telegram", url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`, testId: "share-telegram" },
  ];

  // Channel switch — persist + fetch + if currently playing, swap stream live
  const handleChannelChange = async (newChannel) => {
    if (newChannel === channel) return;
    setChannel(newChannel);
    try { localStorage.setItem(CHANNEL_LS_KEY, newChannel); } catch (e) { /* ignore */ }
    setStreamError("");
    setLoading(true);
    setCurrentMixId(null);
    const wasPlaying = isPlaying;
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);

    // fetch new channel's now-playing directly
    try {
      const { data } = await api.get(`/now-playing?channel=${encodeURIComponent(newChannel)}`);
      setNowPlaying(data);
      if (wasPlaying && data?.playing) {
        loadAndPlay(data);
      }
    } catch (e) {
      console.error("channel switch failed", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-10 relative">
      {/* Top share button */}
      <button
        type="button"
        onClick={handleShareClick}
        data-testid="share-button"
        className="absolute top-6 left-6 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--neon-cyan)] transition-colors flex items-center gap-1.5"
        aria-label="Share station"
      >
        <Share2 size={14} /> Share
      </button>

      {/* Top admin link */}
      <Link
        to="/admin/login"
        data-testid="admin-link"
        className="absolute top-6 right-6 text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-[var(--neon-cyan)] transition-colors flex items-center gap-1.5"
      >
        <Radio size={14} /> DJ Console
      </Link>

      <div className="player-card p-8 md:p-10 w-full max-w-[420px]" data-testid="player-card">
        {/* Live badge */}
        <div className="flex items-center justify-between mb-6">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(255,59,48,0.08)] border border-[rgba(255,59,48,0.35)]">
            <span className="live-dot"></span>
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ff5566]">
              Live Broadcast
            </span>
          </div>
          <div className={`eq-bars ${isPlaying ? "" : "paused"}`}>
            <span></span><span></span><span></span><span></span>
          </div>
        </div>

        {/* Vinyl */}
        <div className="relative w-[220px] h-[220px] mx-auto mb-7">
          <div className={`vinyl-disk ${isPlaying ? "playing" : ""}`} data-testid="vinyl-disk">
            <div className="vinyl-center">MF</div>
          </div>
        </div>

        {/* Station name */}
        <div className="text-center mb-1">
          <h1 className="font-display text-[28px] leading-tight font-bold tracking-tight" data-testid="station-name">
            Mixxflow DJ Radio
          </h1>
        </div>

        {/* Active channel label (replaces the static genre chip row) */}
        <p className="text-center text-[11px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)] mb-5">
          <span className="text-[var(--neon-cyan)]" data-testid="active-channel-label">
            {channel === "all" ? "All Genres" : channel.charAt(0).toUpperCase() + channel.slice(1)}
          </span> · Channel
        </p>

        {/* Channel selector grid */}
        <ChannelSelector value={channel} onChange={handleChannelChange} />

        {/* Now playing info */}
        <div className="text-center mb-6 min-h-[60px] flex flex-col justify-center" data-testid="now-playing-info">
          {loading ? (
            <p className="text-[var(--text-muted)] text-sm font-mono">Tuning in...</p>
          ) : hasPlaylist ? (
            <>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--neon-cyan)] mb-1.5">
                Now Spinning
              </p>
              <p className="text-white font-semibold text-base truncate px-2" data-testid="current-track-title">
                {current.title}
              </p>
              <p className="text-[var(--text-muted)] text-sm font-mono mt-0.5" data-testid="current-track-artist">
                {current.artist}
              </p>
            </>
          ) : (
            <>
              <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-1.5">
                Off Air
              </p>
              <p className="text-[var(--text-muted)] text-sm px-2">
                {nowPlaying?.message || "Awaiting first broadcast..."}
              </p>
            </>
          )}
        </div>

        {/* Play button + volume */}
        <div className="flex items-center justify-center gap-6 mb-6">
          <button
            onClick={() => setMuted((m) => !m)}
            data-testid="mute-button"
            className="text-[var(--text-muted)] hover:text-[var(--neon-cyan)] transition-colors"
            aria-label="Mute"
          >
            {muted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
          </button>

          <button
            className="play-btn"
            onClick={handlePlay}
            disabled={!hasPlaylist}
            data-testid="play-button"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause size={28} fill="currentColor" /> : <Play size={28} fill="currentColor" style={{ marginLeft: 3 }} />}
          </button>

          <input
            type="range"
            min="0" max="1" step="0.01"
            value={volume}
            onChange={(e) => { setVolume(parseFloat(e.target.value)); setMuted(false); }}
            data-testid="volume-slider"
            className="w-16 accent-[var(--neon-cyan)] cursor-pointer"
            aria-label="Volume"
          />
        </div>

        {streamError && (
          <p className="text-center text-xs text-amber-400/80 mb-4 font-mono" data-testid="stream-error">
            {streamError}
          </p>
        )}

        {/* Next up — hide when playlist has only 1 track (loops to itself) */}
        {next && next.id !== current?.id && (
          <div className="border-t border-[var(--border-soft)] pt-4 mb-4 flex items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
            <ChevronRight size={12} className="text-[var(--brand-purple-light)]" />
            <span>Up Next:</span>
            <span className="text-white/80 normal-case tracking-normal" data-testid="next-track-title">
              {next.title}
            </span>
          </div>
        )}

        {/* Brand footer */}
        <div className="border-t border-[var(--border-soft)] pt-5 flex items-center justify-center gap-2">
          <span className="brand-powered">powered by</span>
          <span
            className="brand-javig select-none cursor-pointer relative"
            data-testid="javig-brand"
            {...secretBind}
            style={{
              touchAction: "manipulation",
              filter: isHolding
                ? "drop-shadow(0 0 12px rgba(255, 215, 0, 0.9)) drop-shadow(0 0 24px rgba(212, 175, 55, 0.6))"
                : undefined,
              transition: "filter 0.2s ease",
            }}
            title=""
          >
            JAVI G
            {isHolding && (
              <span
                className="absolute -bottom-1.5 left-0 right-0 h-[2px] rounded-full pointer-events-none"
                style={{
                  background: "linear-gradient(90deg, #ffd700, #d4af37)",
                  width: `${holdProgress}%`,
                  boxShadow: "0 0 8px rgba(255, 215, 0, 0.8)",
                }}
              />
            )}
          </span>
        </div>
      </div>

      {/* Footer mini tag */}
      <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)] mt-8">
        24/7 · Streaming Live · Onyx & Cyan
      </p>

      {/* Listener Voice Drop */}
      <VoiceDropBox />

      {/* Top 40 House Remixes section */}
      <Top40Section />

      {/* Site bottom footer — also a secret trigger for the Royal Request */}
      <footer className="w-full max-w-[1080px] mx-auto mt-8 pt-6 pb-10 px-4 border-t border-[var(--border-soft)]">
        <div className="flex flex-col items-center gap-1.5">
          <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[var(--text-muted)]">
            Mixxflow.Radio · Streaming 24 / 7
          </p>
          <div className="flex items-center gap-2">
            <span className="brand-powered text-sm">powered by</span>
            <span
              className="brand-javig select-none cursor-pointer relative text-lg"
              data-testid="javig-brand-footer"
              {...secretBind}
              style={{
                touchAction: "manipulation",
                filter: isHolding
                  ? "drop-shadow(0 0 12px rgba(255, 215, 0, 0.9)) drop-shadow(0 0 24px rgba(212, 175, 55, 0.6))"
                  : undefined,
                transition: "filter 0.2s ease",
              }}
            >
              JAVI G
              {isHolding && (
                <span
                  className="absolute -bottom-1.5 left-0 right-0 h-[2px] rounded-full pointer-events-none"
                  style={{
                    background: "linear-gradient(90deg, #ffd700, #d4af37)",
                    width: `${holdProgress}%`,
                    boxShadow: "0 0 8px rgba(255, 215, 0, 0.8)",
                  }}
                />
              )}
            </span>
          </div>
        </div>
      </footer>

      <audio
        ref={audioRef}
        onEnded={handleTrackEnded}
        onError={() => setStreamError("Stream error. Try again.")}
        preload="none"
      />

      {/* Royal Request modal — unlocked via the secret tap-tap-tap-hold combo */}
      <KingRequestModal open={kingOpen} onClose={() => setKingOpen(false)} />

      {/* Share popover modal — only shown when Web Share API unavailable / dismissed */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setShareOpen(false)}
          data-testid="share-modal"
        >
          <div
            className="player-card w-full max-w-[380px] p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setShareOpen(false)}
              data-testid="share-close-button"
              className="absolute top-3 right-3 text-[var(--text-muted)] hover:text-white transition-colors"
              aria-label="Close share"
            >
              <X size={18} />
            </button>

            <div className="flex items-center gap-2 mb-1">
              <Share2 size={16} className="text-[var(--neon-cyan)]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--neon-cyan)]">
                Spread the Frequency
              </span>
            </div>
            <h3 className="font-display text-xl font-bold mb-4">Share Mixxflow</h3>

            {/* Tab switcher */}
            <div className="flex items-center gap-1 mb-5 p-1 rounded-md bg-[#0a0a14] border border-[var(--border-soft)]">
              {[
                { id: "share", label: "Social", icon: <Share2 size={12} /> },
                { id: "qr", label: "QR Code", icon: <QrCode size={12} /> },
                { id: "embed", label: "Embed", icon: <Code2 size={12} /> },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setShareTab(t.id)}
                  data-testid={`share-tab-${t.id}`}
                  className={`flex-1 py-1.5 rounded text-[11px] font-mono uppercase tracking-widest flex items-center justify-center gap-1.5 transition-all ${
                    shareTab === t.id
                      ? "bg-[rgba(0,242,254,0.1)] text-[var(--neon-cyan)] border border-[var(--border-cyan)]"
                      : "text-[var(--text-muted)] hover:text-white border border-transparent"
                  }`}
                >
                  {t.icon} {t.label}
                </button>
              ))}
            </div>

            {/* Tab: Social share */}
            {shareTab === "share" && (
              <div data-testid="share-tab-content-share">
                <p className="text-[var(--text-muted)] text-sm mb-4 leading-relaxed">
                  {current ? `Tell the world about "${current.title}" by ${current.artist}.` : "Bring more listeners to the party."}
                </p>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {shareLinks.map((s) => (
                    <a
                      key={s.name}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={s.testId}
                      className="text-center text-sm py-2.5 rounded-md border border-[var(--border-soft)] hover:border-[var(--neon-cyan)] hover:bg-[rgba(0,242,254,0.05)] transition-all text-white/90 font-mono"
                      onClick={() => setTimeout(() => setShareOpen(false), 200)}
                    >
                      {s.name}
                    </a>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={handleCopyLink}
                  data-testid="share-copy-button"
                  className="w-full py-2.5 rounded-md border border-[var(--border-cyan)] bg-[rgba(0,242,254,0.05)] text-[var(--neon-cyan)] font-mono text-sm flex items-center justify-center gap-2 hover:bg-[rgba(0,242,254,0.1)] transition-all"
                >
                  {shareCopied ? (
                    <><Check size={14} /> Copied to clipboard!</>
                  ) : (
                    <><Copy size={14} /> Copy link</>
                  )}
                </button>
              </div>
            )}

            {/* Tab: QR code */}
            {shareTab === "qr" && (
              <div className="flex flex-col items-center" data-testid="share-tab-content-qr">
                <p className="text-[var(--text-muted)] text-sm mb-4 text-center leading-relaxed">
                  Project this at parties — listeners scan and tune in instantly.
                </p>
                <div className="p-4 bg-white rounded-md mb-4" data-testid="share-qr-code">
                  <QRCodeSVG
                    value={stationUrl}
                    size={200}
                    bgColor="#ffffff"
                    fgColor="#09090e"
                    level="M"
                    includeMargin={false}
                  />
                </div>
                <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] text-center break-all">
                  {stationUrl.replace(/^https?:\/\//, "")}
                </p>
              </div>
            )}

            {/* Tab: Embed code */}
            {shareTab === "embed" && (
              <div data-testid="share-tab-content-embed">
                <p className="text-[var(--text-muted)] text-sm mb-3 leading-relaxed">
                  Paste this snippet into your website, blog or Linktree to embed a live mini player.
                </p>
                <textarea
                  readOnly
                  value={embedSnippet}
                  onClick={(e) => e.target.select()}
                  data-testid="share-embed-snippet"
                  className="input-dark font-mono text-[11px] leading-relaxed w-full h-28 resize-none mb-3"
                  spellCheck="false"
                />
                <button
                  type="button"
                  onClick={handleCopyEmbed}
                  data-testid="share-embed-copy"
                  className="w-full py-2.5 rounded-md border border-[var(--border-cyan)] bg-[rgba(0,242,254,0.05)] text-[var(--neon-cyan)] font-mono text-sm flex items-center justify-center gap-2 hover:bg-[rgba(0,242,254,0.1)] transition-all"
                >
                  {embedCopied ? (
                    <><Check size={14} /> Snippet copied!</>
                  ) : (
                    <><Copy size={14} /> Copy embed code</>
                  )}
                </button>
                <a
                  href={embedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="share-embed-preview"
                  className="block text-center text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--brand-purple-light)] hover:text-[var(--neon-cyan)] mt-3"
                >
                  Preview embed in new tab ↗
                </a>
              </div>
            )}

            <p className="text-center text-[10px] font-mono uppercase tracking-[0.25em] text-[var(--text-muted)] mt-5">
              powered by <span className="text-[var(--brand-purple-light)]">JAVI G</span>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
